import { PersonDTO } from "@/api/dto/PersonDTO";
import { Address } from "../Address";
import { EmergencyContact } from "./EmergencyContact";
import { MemberCard } from "./MemberCard";
import { PersonMeta } from "./PersonMeta";
import { PersonsMembershipsDTO } from "@/api/dto/PersonsMembershipsDTO";
import {
  AddressDTO,
  AddressDTOTracked,
  CardDTO,
  DamageWaiverDTO,
  MembershipDTO,
  PersonDTOTracked,
  PersonPublicChildDTO,
  PersonPublicChildDTOTracked,
  PersonPublicDTO,
  PersonPublicParentDTO,
  PersonPublicParentDTOOptional,
  PersonPublicParentDTOTracked,
  PersonPublicRelatedDTO,
  PersonPublicRelatedDTOOptional,
  PersonPublicRelatedDTOTracked,
  PersonRef,
  PersonReferralDTO,
  PersonTypeEnum,
  PersonWithRelationsDTO,
  PhoneDTO,
  PhoneDTOTracked,
  SwimProof,
} from "@/autogen";

export type PersonFormType =
  | PersonPublicChildDTOTracked
  | PersonPublicParentDTOTracked
  | PersonPublicRelatedDTOTracked;

export type PersonPropsType = PersonPublicChildDTO | PersonPublicDTO;

// Top level details used for creating a person
export interface IPersonFragment extends PersonPublicParentDTOOptional {
  id?: number;
  //lastName: string | null;
  //type: number | null;
  //dob: string | null;
  //dobVerified: boolean | null;
  //email: string | null;
  //primaryPhone?: string | null;
  //primaryPhoneType?: string | null;
  meta?: PersonMeta;
}

// Top level details and all others combined
export interface IPerson extends IPersonFragment, PersonPublicDTO {
  //updatedOn: Date;
  assignId: any;
  personId: number;
  temp: string;
  middle?: string | undefined;
  prefix?: string | undefined;
  suffix?: string | undefined;
  employer?: string | undefined;
  addresses?: AddressDTO[];
  phones?: PhoneDTO[];
  personType?: PersonTypeEnum;
  //address: Address;
  //emergencyContacts: PersonPublicRelatedDTO[];
  //cards: MemberCard[];
  currentMemberships?: MembershipDTO[];
  currentCards?: CardDTO[];
  currentDamageWaivers?: DamageWaiverDTO[];
  //purchaseRecipients: PersonPublicRelatedDTO[];
  referrals?: PersonReferralDTO[];
  //children: PersonPublicChildDTO[];
  //currentCards: CardDTO[];
  //relationships: [];
  //alternatePhone?: string | null;
  //alternatePhoneType?: string | null;
  adminHold: boolean | undefined;
  gender?: string | undefined;
  citizenship?: string | undefined;
  ethnicity?: string | undefined;
  language?: string | undefined;
  student?: boolean | undefined;
  //dto: PersonDTO | null;
}

/**
 * Complete person details
 */
export class Person implements IPerson {
  assignId: number;
  id;
  personId: number;
  first;
  last;
  middle;
  prefix;
  suffix;
  personType;
  type;
  dob;
  dobVerified;
  email;
  employer;
  //dto;
  //address;
  //emergencyContacts;
  //cards;
  currentMemberships;
  currentCards;
  currentDamageWaivers;
  //relationships;
  //primaryPhone?: string;
  //primaryPhoneType?: string;
  //alternatePhone;
  //alternatePhoneType;
  phones;
  addresses;
  adminHold;
  gender;
  citizenship;
  ethnicity;
  language;
  student;
  meta: PersonMeta;

  constructor(config: IPerson) {
    this.assignId = config.assignId;
    Object.assign(this, config);
    this.id = config.id;
    this.personId = config.id!!;
    this.first = config.first;
    this.last = config.last;
    this.middle = config.middle;
    this.prefix = config.prefix;
    this.suffix = config.suffix;
    this.type = config.personType;
    this.personType = config.personType;
    this.dob = config.dob;
    this.dobVerified = config.dobVerified;
    this.email = config.email;
    this.employer = config.employer;
    this.swimProof = config.swimProof;
    //this.address = config.address;
    //this.emergencyContacts = config.emergencyContacts;
    //this.cards = config.cards;
    this.currentMemberships = config.currentMemberships;
    this.currentCards = config.currentCards;
    this.currentDamageWaivers = config.currentDamageWaivers;
    //this.relationships = config.relationships;
    this.phones = config.phones;
    this.addresses = config.addresses;
    //this.purchaseRecipients = config.purchaseRecipients;
    this.referrals = config.referrals;
    //this.children = config.children;
    //this.currentCards = config.currentCards;
    //this.primaryPhone = config.primaryPhone;
    //this.primaryPhoneType = config.primaryPhoneType;
    //this.alternatePhone = config.alternatePhone;
    //this.alternatePhoneType = config.alternatePhoneType;
    this.adminHold = config.adminHold;
    this.gender = config.gender;
    this.citizenship = config.citizenship;
    this.ethnicity = config.ethnicity;
    this.language = config.language;
    this.student = config.student;
    this.meta = config.meta || ({} as PersonMeta);

    //this.dto = config.dto;
  }
  //purchaseRecipients: PersonPublicRelatedDTO[];
  referrals?: PersonReferralDTO[];
  //children: PersonPublicChildDTO[];
  //currentCards: CardDTO[];
  "allowSignouts"?: boolean | undefined;
  "allergies"?: string | undefined;
  "medications"?: string | undefined;
  "specialNeeds"?: string | undefined;
  "occupation"?: string | undefined;
  "matchingGifts"?: string | undefined;
  "organization"?: string | undefined;
  "school"?: string | undefined;
  "military"?: string | undefined;
  "reducedLunch"?: boolean | undefined;
  "previousMember"?: boolean | undefined;
  "swimProof"?: SwimProof | undefined;
  "isLocked"?: boolean | undefined;
  "preferred"?: string | undefined;
  "stripeCustomerId"?: string | undefined;
  "temp": string;

  get fullName() {
    return `${this.first} ${this.last}`;
  }
}

type WithIsSet<T> = {
  [K in keyof T as `${string & K}IsSet`]?: boolean;
};

/**
 * Only top level details for a person
 */
export class PersonFragment implements Partial<IPersonFragment> {
  id?;
  firstName?;
  lastName?;
  middle?;
  last?;
  first?;
  preferred?;
  prefix?;
  suffix?;

  phones?;
  addresses?;
  emergencyContacts?: PersonPublicRelatedDTOOptional[];
  language?;
  referrals?;
  type?;
  dob?;
  dobVerified?: boolean | undefined;
  email?;
  primaryPhone?: string;
  primaryPhoneType?: string;
  meta?;
  "temp": string;

  constructor(config: IPersonFragment) {
    this.id = config.id;
    this.firstName = config.first || undefined;
    this.lastName = config.last || null;
    this.type = config.personType || null;
    this.dob = config.dob || undefined;
    this.dobVerified = config.dobVerified || undefined;
    this.email = config.email || undefined;
    this.phones = config.phones;
    this.addresses = config.addresses;
    this.emergencyContacts = config.emergencyContacts;
    this.language = config.language;
    this.referrals = config.referrals;
    this.last = config.last;
    this.first = config.first;
    this.middle = config.middle;
    this.preferred = config.preferred;
    this.prefix = config.prefix;
    this.suffix = config.suffix;
    //this.primaryPhone = config.primaryPhone || null;
    //this.primaryPhoneType = config.primaryPhoneType || null;
    this.meta = config.meta;
    this.temp = config.temp || "N";
  }
}
